import { Component, computed, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BxLogger } from '../../../../../core/services/bx-logger.service';
import { BxUnverifiedAlertService } from '../../../../../core/services/bx-unverified-alert.service';
import { DialogService } from '../../../../../core/services/dialog.service';
import { InvoiceSharingService } from '../../../../../core/services/invoice-sharing.service';
import { PermissionService } from '../../../../../core/services/permission/permission.service';
import { DocumentTypes, Job, JobPayment } from '../../../../../data/models';
import { RepoJobsService } from '../../../../../data/repositories/repo-jobs.service';
import { SharedDataService } from '../../../../../data/services/shared-data.service';
import { ReportService } from '../../../../../reports/report.service';
import { CustomLayoutsService } from '../../../../estimates/custom-layouts/custom-layouts.service';
import { InvoicesReceiptService } from '../../../invoices/invoices-receipt.service';
import { InvoicesService } from '../../../invoices/invoices.service';

@Component({
    selector: 'app-job-invoices-completion-percent',
    templateUrl: './job-invoices-completion-percent.component.html',
    styleUrls: [
        '../../../../../../../src/app/shared/composable-styles/input.scss',
        '../../../../../../../src/app/shared/composable-styles/button.scss',
    ],
    standalone: false,
})
export class JobInvoicesCompletionPercentComponent implements OnInit {
    // @ts-ignore TS2564
    @Input() job: Job;
    @Output() invoiceChange = new EventEmitter();

    filter = 'All';
    taxCode = '';
    tableHeight = 'auto';

    lastSharedInvoiceStatus = '';
    generatingPDF = false;
    pdfUrl = '';
    translations = {
        variations: '',
        retentionTotalTooltip: '',
        variationsExTitle: '',
    };

    #usingAdjustments: boolean;

    jobPayments = signal<JobPayment[]>([]);
    statusFilter = signal('All');
    searchText = signal<string>('');

    rows = computed(() => {
        if (this.statusFilter() == 'All') {
            return this.searchFilteredInvoices();
        }

        const statusFilteredInvoices = this.searchFilteredInvoices().filter((invoice) => {
            return this.statusFilter() == invoice.Status;
        });

        return statusFilteredInvoices;
    });

    searchFilteredInvoices = computed(() => {
        const searchTerm = this.searchText().toLowerCase();
        if (searchTerm.length > 0) {
            const searchFilteredOrders =
                this.jobPayments()?.filter((payment) => {
                    return (
                        payment.Description.toLowerCase().includes(searchTerm) ||
                        payment.InvoiceNumber.toString().toLowerCase().includes(searchTerm)
                    );
                }) ?? [];

            return searchFilteredOrders;
        }

        return this.jobPayments();
    });

    invoiceCounts = computed(() => {
        const counts = {
            all: 0,
            pending: 0,
            invoiced: 0,
            partreceived: 0,
            received: 0,
        };

        this.searchFilteredInvoices().forEach((p) => {
            counts.all++;
            switch (p.Status) {
                case 'Pending':
                    counts.pending++;
                    break;
                case 'Invoiced':
                    counts.invoiced++;
                    break;
                case 'PartReceived':
                    counts.partreceived++;
                    break;
                case 'Received':
                    counts.received++;
                    break;
            }
        });

        return counts;
    });

    constructor(
        private reportService: ReportService,
        private bxUnverifiedAlertService: BxUnverifiedAlertService,
        private invoiceSharingService: InvoiceSharingService,
        public permissionService: PermissionService,
        private logger: BxLogger,
        private translateService: TranslateService,
        private invoicesService: InvoicesService,
        private customLayoutsService: CustomLayoutsService,
        private invoicesReceiptService: InvoicesReceiptService,
        private sharedData: SharedDataService,
        private repoJobs: RepoJobsService,
        private dialogService: DialogService
    ) {
        this.#usingAdjustments =
            this.sharedData.UserData.tenant.BillingCountry === 'New Zealand' ||
            this.sharedData.UserData.tenant.BillingCountry === 'Australia';
    }

    ngOnInit(): void {
        const text = this.translateService.instant('general.variations');
        this.translations.variations = text.toLowerCase();
        this.translations.retentionTotalTooltip = `${this.job.RetentionPercentage}% of the contract total (${
            this.job.RetentionType === 0 ? 'excl' : 'incl'
        } ${this.translations.variations})`;
        this.translations.variationsExTitle = `${text} (Ex)`;
        this.load();
    }

    handleSearchInput(event: Event) {
        const searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.searchText.set(searchTerm);
    }

    onInvoiceChange() {
        this.invoiceChange.emit();
    }

    load() {
        this.jobPayments.set(
            this.job.JobPayments?.filter((p) => {
                return !p.IsDeleted && !p.IsDepositInvoice && !p.IsRetentionInvoice;
            }) ?? []
        );
    }

    isEstimateNotAccepted() {
        return this.job.Estimate && !this.job.Estimate.IsAccepted;
    }

    saveJob() {
        if (this.job.InvoiceDueDays || this.job.InvoiceDueDays === 0) this.repoJobs.Save(this.job, 'Default due days updated');
    }

    viewInvoice(invoice: JobPayment) {
        this.invoicesService.viewInvoice(invoice).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            (error) => ({})
        );
    }

    addInvoice() {
        this.invoicesService.newInvoice(this.job).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    deleteInvoice(evt: MouseEvent, invoice: JobPayment) {
        evt.stopPropagation();
        const params = {
            title: 'Delete invoice?',
            text: "If you go ahead, you won't be able to get it back.",
        };
        this.dialogService.show(params).then(
            () => {
                invoice.IsDeleted = true;
                this.repoJobs.Save(null, 'Invoice has been deleted');
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    printJobClaim() {
        const reportName = this.#usingAdjustments ? 'jobclaimv2' : 'jobclaim';
        const params = {
            reportName,
            reportTitle: 'Invoice Statement',
            params: {
                ID_Job: this.job.ID_Job,
                CalculationMethod: this.job.CalculationMethod,
            },
            fileName: this.job.Number ? this.job.Number + '-InvoiceStatement' : '',
        };
        this.reportService.generate(params);
    }

    printInvoice(invoice: JobPayment) {
        this.invoicesService.generateInvoice(invoice, undefined, true);
    }

    shareInvoice(invoice: JobPayment) {
        this.lastSharedInvoiceStatus = invoice.Status;
        const documentSource = { Id: this.job.ID_Job, Type: 'Job' };
        this.invoiceSharingService.getEmailConfig(invoice, 'pdf').then((emailConfig) => {
            const shareConfig = this.invoiceSharingService.getShareConfig(invoice);
            this.customLayoutsService.openDigitalAcceptanceShareForm(
                emailConfig,
                shareConfig,
                null,
                true,
                DocumentTypes.Invoice,
                false,
                this.job.ID_Lead,
                documentSource,
                true,
                invoice,
                () => {
                    this.handleShareSuccess();
                },
                () => {
                    this.resetInvoiceStatus(invoice);
                }
            );
        });
    }

    // TODO:: move to shared component to slim the product
    emailJobClaim() {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.invoicesService.emailJobClaim(this.job);
    }

    // TODO:: move to shared component to slim the product
    emailInvoice(invoice: JobPayment) {
        if (!this.sharedData.UserData.identityInfo.email_verified) {
            this.bxUnverifiedAlertService.trigger(this.sharedData.UserData.user);
            return;
        }

        this.invoicesService.emailInvoice(invoice).then(
            () => {
                this.load();
                this.invoiceChange.emit();
            },
            () => ({})
        );
    }

    termsAndConditions() {
        this.invoicesService.viewTermsAndConditions();
    }

    addReceipt(invoice: JobPayment) {
        this.invoicesReceiptService.addReceipt(invoice).then(
            () => {
                this.repoJobs.Save(null, 'Receipt saved');
                this.invoiceChange.emit();
            },
            () => {
                invoice.entityAspect.rejectChanges();
            }
        );
    }

    handleShareSuccess() {
        this.logger.success('Invoice Shared');
    }

    resetInvoiceStatus(invoice: JobPayment) {
        if (this.lastSharedInvoiceStatus !== invoice.Status) {
            invoice.Status = this.lastSharedInvoiceStatus;
            this.invoicesService.setInvoiceNumber(invoice).then((invoiceWithId) => {
                this.invoicesService.saveInvoice(invoiceWithId);
                this.invoiceChange.emit();
            });
        }
    }
}
